import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  AlertTriangle, CheckCircle, XCircle, RefreshCw, ChevronDown, ChevronUp,
  DollarSign, Clock, TrendingUp, Shield, Users, FileText, Info,
} from "lucide-react";
import { API } from "@/config";

const money = (n) => (n == null ? "—" : `$${Number(n).toFixed(2)}`);
const TIER_COLORS = {
  "DATABASE_CALCULATION": "text-emerald-300 bg-emerald-900/30 border-emerald-700/40",
  "STRIPE_TEST_REQUIRED":  "text-amber-300 bg-amber-900/30 border-amber-700/40",
  "STRIPE_TEST_REQUIRED":  "text-amber-300 bg-amber-900/30 border-amber-700/40",
  "LIVE_VERIFICATION_REQUIRED": "text-red-300 bg-red-900/30 border-red-700/40",
  "NOT VERIFIED":          "text-amber-300 bg-amber-900/30 border-amber-700/40",
  "VERIFIED":              "text-emerald-300 bg-emerald-900/30 border-emerald-700/40",
};

function VerificationBadge({ label }) {
  const tier = Object.keys(TIER_COLORS).find((k) => label?.toUpperCase().includes(k)) || "NOT VERIFIED";
  const cls = TIER_COLORS[tier] || "text-gray-300 bg-gray-800 border-gray-600";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-mono border ${cls}`}>
      {tier.includes("VERIFIED") && !tier.includes("NOT") ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
      {label}
    </span>
  );
}

function ReconciliationRow({ label, value, highlight }) {
  return (
    <div className={`flex justify-between py-1 border-b border-[#FFD166]/10 ${highlight ? "text-[#FFD166] font-semibold" : "text-[#F6F1E4]"}`}>
      <span className="text-[#B9C8D5] text-sm">{label}</span>
      <span className="font-mono text-sm">{money(value)}</span>
    </div>
  );
}

function PayoutRow({ entry, idx }) {
  const statusColor =
    entry.status?.includes("PROCESSED") ? "text-emerald-300" :
    entry.status?.includes("CANCELLED") ? "text-red-300" : "text-amber-300";
  return (
    <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 py-2 border-b border-[#FFD166]/8 text-sm">
      <div>
        <div className="text-[#F6F1E4] font-medium">
          {entry.kind || entry.installment || `Installment ${idx + 1}`}
        </div>
        <div className="text-[#B9C8D5]/80 text-xs mt-0.5">
          {entry.description || entry.note || entry.scheduled_label || entry.scheduled_for || ""}
        </div>
      </div>
      <div className="text-right">
        <div className="text-[#B9C8D5] text-xs">Gross</div>
        <div className="font-mono">{money(entry.gross_amount || entry.gross)}</div>
      </div>
      <div className="text-right">
        <div className="text-red-300 text-xs">Reserve</div>
        <div className="font-mono text-red-300">−{money(entry.federal_reserve_deducted ?? entry.reserve ?? 0)}</div>
      </div>
      <div className="text-right">
        <div className="text-emerald-300 text-xs">Net</div>
        <div className={`font-mono font-semibold ${entry.net_to_bank > 0 || entry.net > 0 ? "text-emerald-300" : "text-[#B9C8D5]/60"}`}>
          {money(entry.net_to_bank ?? entry.net ?? entry.amount)}
        </div>
      </div>
      <div className="col-span-4">
        <span className={`text-xs font-mono ${statusColor}`}>{entry.status || ""}</span>
      </div>
    </div>
  );
}

function ReconciliationBox({ recon }) {
  if (!recon) return null;
  return (
    <div className="mt-4 p-3 bg-black/30 rounded-lg border border-[#FFD166]/15">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-4 h-4 text-[#FFD166]" />
        <span className="font-medium text-sm">Reconciliation</span>
        <span className={`ml-2 text-xs px-2 py-0.5 rounded ${recon.balanced ? "bg-emerald-900/40 text-emerald-300" : "bg-red-900/40 text-red-300"}`}>
          {recon.balanced ? "BALANCED ✓" : `GAP: ${money(recon.gap)}`}
        </span>
      </div>
      <ReconciliationRow label="Stripe Total Received"    value={recon.stripe_total_received}    />
      <ReconciliationRow label="FL Sales Tax Retained"    value={recon.fl_sales_tax_retained}    />
      <ReconciliationRow label="Federal Reserve Retained" value={recon.federal_reserve_retained}  />
      {(recon.affiliate_commission_owed > 0 || recon.affiliate_commission_paid > 0) && (
        <ReconciliationRow
          label={`Affiliate Commission ${recon.affiliate_commission_paid > 0 ? "(Paid)" : "(Owed)"}`}
          value={recon.affiliate_commission_owed || recon.affiliate_commission_paid}
        />
      )}
      {recon.extra && Object.entries(recon.extra).map(([k, v]) => (
        <ReconciliationRow key={k} label={k} value={v} />
      ))}
      <ReconciliationRow label="Net to Bank (Total)"     value={recon.net_to_bank} highlight />
      <div className="mt-2 text-xs text-[#B9C8D5]/70 font-mono break-all">{recon.equation}</div>
    </div>
  );
}

function StripeVerification({ info }) {
  if (!info) return null;
  return (
    <div className="mt-3 p-3 bg-amber-950/20 rounded-lg border border-amber-700/25">
      <div className="flex items-center gap-2 mb-2">
        <Shield className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-medium text-amber-300">Stripe Verification Status</span>
        <span className={`text-xs font-mono px-1.5 py-0.5 rounded ${info.payout_sweep_enabled ? "bg-red-900/50 text-red-300" : "bg-emerald-900/40 text-emerald-300"}`}>
          payout_sweep_enabled = {String(info.payout_sweep_enabled)}
        </span>
      </div>
      <div className="space-y-1">
        {Object.entries(info.verification_tiers || {}).map(([k, v]) => (
          <div key={k} className="flex items-start gap-2 text-xs">
            {v.startsWith("VERIFIED") ? <CheckCircle className="w-3 h-3 text-emerald-400 mt-0.5 shrink-0" /> : <XCircle className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />}
            <span className="text-[#B9C8D5]/80">{v}</span>
          </div>
        ))}
      </div>
      {info.required_before_live?.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-amber-300 font-medium mb-1">Required before live activation:</div>
          <ul className="list-disc list-inside space-y-0.5">
            {info.required_before_live.map((item, i) => (
              <li key={i} className="text-xs text-[#B9C8D5]/70">{item}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const SCENARIO_ICONS = {
  S1: DollarSign, S2: Users, S3: RefreshCw, S4: AlertTriangle, S5: Shield, S6: TrendingUp,
};
const SCENARIO_COLORS = {
  S1: "border-blue-600/30 bg-blue-950/10",
  S2: "border-purple-600/30 bg-purple-950/10",
  S3: "border-orange-600/30 bg-orange-950/10",
  S4: "border-amber-600/30 bg-amber-950/10",
  S5: "border-red-600/30 bg-red-950/10",
  S6: "border-green-600/30 bg-green-950/10",
};

function ScenarioCard({ scenario }) {
  const [open, setOpen] = useState(false);
  const Icon = SCENARIO_ICONS[scenario.scenario_id] || FileText;
  const borderCls = SCENARIO_COLORS[scenario.scenario_id] || "";
  const hasError = !!scenario.error;

  return (
    <div className={`rounded-xl border ${borderCls} overflow-hidden`} data-testid={`scenario-card-${scenario.scenario_id}`}>
      {/* Header */}
      <button
        className="w-full flex items-center gap-3 p-4 hover:bg-white/3 transition-colors text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#FFD166]" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-[#F6F1E4]">{scenario.scenario_id} — {scenario.title}</div>
          {scenario.description && (
            <div className="text-xs text-[#B9C8D5]/70 mt-0.5 line-clamp-2">{scenario.description}</div>
          )}
          {hasError && <div className="text-xs text-red-400 mt-0.5">Error: {scenario.error}</div>}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {!hasError && scenario.reconciliation && (
            <span className={`text-xs px-2 py-0.5 rounded font-mono border ${
              scenario.reconciliation?.balanced
                ? "bg-emerald-900/30 text-emerald-300 border-emerald-700/30"
                : "bg-red-900/30 text-red-300 border-red-700/30"
            }`}>
              {scenario.reconciliation?.balanced ? "✓ Balanced" : "⚠ Gap"}
            </span>
          )}
          {open ? <ChevronUp className="w-4 h-4 text-[#B9C8D5]" /> : <ChevronDown className="w-4 h-4 text-[#B9C8D5]" />}
        </div>
      </button>

      {open && !hasError && (
        <div className="px-4 pb-5 space-y-4 border-t border-white/5">
          {/* Order Summary */}
          {scenario.order && (
            <div className="mt-4">
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Order Summary</div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm p-3 bg-black/20 rounded-lg border border-white/5">
                {scenario.order.products?.length > 0 && (
                  <div className="col-span-2 flex justify-between border-b border-white/5 py-1">
                    <span className="text-[#B9C8D5]">Products</span>
                    <span className="text-right">{scenario.order.products.join(" · ")}</span>
                  </div>
                )}
                {[
                  ["Rental Subtotal", scenario.order.rental_subtotal],
                  ["Enchanted Care", scenario.order.enchanted_care],
                  ["PoM Fee", scenario.order.pom_fee],
                  ["Partner Discount", scenario.order.partner_discount && scenario.order.partner_discount < 0 ? scenario.order.partner_discount : null],
                  ["Delivery Fee", scenario.order.delivery_fee],
                  ["FL Sales Tax", scenario.order.sales_tax],
                  ["Partner Commission", scenario.order.partner_commission],
                  ["Order Total", scenario.order.order_total],
                ].filter(([, v]) => v != null && v !== 0).map(([label, value]) => (
                  <div key={label} className={`flex justify-between border-b border-white/5 py-1 ${label === "Order Total" ? "col-span-2 text-[#FFD166] font-semibold" : ""}`}>
                    <span className="text-[#B9C8D5]">{label}</span>
                    <span className="font-mono">{value < 0 ? `−${money(Math.abs(value))}` : money(value)}</span>
                  </div>
                ))}
                {scenario.order.partner && (
                  <div className="col-span-2 py-1 text-xs text-[#B9C8D5]/70 italic">{scenario.order.partner}</div>
                )}
              </div>
            </div>
          )}

          {/* Revenue Base */}
          {scenario.revenue_base && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Revenue Base &amp; Federal Reserve</div>
              <div className="p-3 bg-black/20 rounded-lg border border-white/5 space-y-1 text-sm">
                <ReconciliationRow label="Total Collected"           value={scenario.revenue_base.total_collected || scenario.revenue_base.net_revenue} />
                {scenario.revenue_base.less_sales_tax > 0 && <ReconciliationRow label="Less: FL Sales Tax"  value={scenario.revenue_base.less_sales_tax} />}
                {scenario.revenue_base.less_unpaid_commission > 0 && <ReconciliationRow label="Less: Unpaid Commission" value={scenario.revenue_base.less_unpaid_commission} />}
                <ReconciliationRow label="Net Revenue (reserve base)" value={scenario.revenue_base.net_revenue}               highlight />
                <ReconciliationRow label={`Federal Reserve (${scenario.revenue_base.federal_reserve_rate || "25%"})`} value={scenario.revenue_base.federal_reserve_amount || scenario.revenue_base.federal_reserve_required} />
                {scenario.revenue_base.outstanding_reserve != null && (
                  <ReconciliationRow label="Outstanding Reserve (after history)" value={scenario.revenue_base.outstanding_reserve} />
                )}
                {scenario.revenue_base.note && (
                  <div className="text-xs text-[#B9C8D5]/70 italic pt-1">{scenario.revenue_base.note}</div>
                )}
              </div>
            </div>
          )}

          {/* Before Action (S3, S4, S5) */}
          {scenario.before_action && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">State Before Action</div>
              <div className="p-3 bg-black/20 rounded-lg border border-orange-700/20 space-y-2 text-sm">
                <p className="text-[#B9C8D5]/80 text-xs">{scenario.before_action.description}</p>
                {scenario.before_action.cancelled?.length > 0 && (
                  <div>
                    <div className="text-xs text-red-400 font-medium mb-1">Cancelled</div>
                    {scenario.before_action.cancelled.map((e, i) => (
                      <PayoutRow key={i} entry={{ ...e, status: "CANCELLED", net_to_bank: e.net }} idx={i} />
                    ))}
                  </div>
                )}
                {scenario.before_action.pending?.length > 0 && (
                  <div>
                    <div className="text-xs text-amber-400 font-medium mb-1">Still Pending</div>
                    {scenario.before_action.pending.map((e, i) => (
                      <PayoutRow key={i} entry={{ ...e, status: "PENDING", net_to_bank: e.net }} idx={i} />
                    ))}
                  </div>
                )}
                <div className="flex gap-6 pt-1 text-xs">
                  <span className="text-[#B9C8D5]">Already paid: <strong className="text-[#F6F1E4]">{money(scenario.before_action.already_paid)}</strong></span>
                  <span className="text-[#B9C8D5]">Outstanding reserve: <strong className="text-red-300">{money(scenario.before_action.outstanding_reserve)}</strong></span>
                </div>
              </div>
            </div>
          )}

          {/* Action taken */}
          {scenario.action && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Action Taken</div>
              <div className="p-3 bg-blue-950/20 rounded-lg border border-blue-700/20 text-sm">
                <span className={`inline-block px-2 py-0.5 rounded text-xs font-mono mr-2 bg-blue-900/40 text-blue-300`}>
                  {(scenario.action.type || "").toUpperCase()}
                </span>
                <span className="text-[#F6F1E4]">{scenario.action.description}</span>
                {scenario.action.refund_amount > 0 && (
                  <div className="mt-1 text-xs text-[#B9C8D5]">
                    Refund amount: <strong className="text-red-300">{money(scenario.action.refund_amount)}</strong>
                    {scenario.action.reason && <span> — {scenario.action.reason}</span>}
                  </div>
                )}
                {scenario.action.payment_reference && (
                  <div className="mt-1 text-xs text-[#B9C8D5]">
                    Ref: <strong className="text-emerald-300">{scenario.action.payment_reference}</strong>
                    {scenario.action.payment_method && <span> via {scenario.action.payment_method}</span>}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payout Schedule */}
          {scenario.payout_schedule?.length > 0 && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Payout Schedule</div>
              <div className="bg-black/20 rounded-lg border border-white/5 p-3">
                {scenario.payout_schedule.map((entry, i) => (
                  <PayoutRow key={i} entry={entry} idx={i} />
                ))}
              </div>
            </div>
          )}

          {/* After Action (recalculate result) */}
          {scenario.after_action && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">After Recalculation</div>
              <div className="p-3 bg-emerald-950/20 rounded-lg border border-emerald-700/20">
                <p className="text-[#B9C8D5]/80 text-xs mb-2">{scenario.after_action.description}</p>
                {scenario.after_action.new_entries?.map((e, i) => (
                  <PayoutRow key={i} entry={{ ...e, net_to_bank: e.net }} idx={i} />
                ))}
                {scenario.after_action.note && (
                  <div className="text-xs text-emerald-300/80 italic mt-2">{scenario.after_action.note}</div>
                )}
              </div>
            </div>
          )}

          {/* Payout history (S4, S5, S6) */}
          {scenario.payout_history && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Payout History (Processed)</div>
              <div className="bg-black/20 rounded-lg border border-white/5 p-3">
                {Object.entries(scenario.payout_history).map(([k, e]) => (
                  <PayoutRow key={k} entry={{ ...e, net_to_bank: e.net, status: e.kind }} idx={0} />
                ))}
              </div>
            </div>
          )}

          {/* First installment history (S5) */}
          {scenario.first_installment_history && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">First Installment (Processed — Old System)</div>
              <div className="p-3 bg-red-950/20 rounded-lg border border-red-700/20 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-[#B9C8D5]">Status</span>
                  <span className="text-amber-300 font-mono text-xs">{scenario.first_installment_history.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#B9C8D5]">Gross paid</span>
                  <span className="font-mono">{money(scenario.first_installment_history.gross_paid)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#B9C8D5]">Federal reserve deducted</span>
                  <span className="font-mono text-red-300">{money(scenario.first_installment_history.federal_reserve_deducted)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[#B9C8D5]">Net received by bank</span>
                  <span className="font-mono text-emerald-300">{money(scenario.first_installment_history.net_received_by_bank)}</span>
                </div>
                {scenario.first_installment_history.warning && (
                  <div className="text-xs text-amber-300 italic pt-1">⚠ {scenario.first_installment_history.warning}</div>
                )}
              </div>
            </div>
          )}

          {/* Recalculate details */}
          {scenario.recalculate && (
            <div>
              <div className="text-xs text-[#FFD166] font-semibold uppercase tracking-wider mb-2">Recalculation</div>
              <div className="p-3 bg-black/20 rounded-lg border border-white/5 space-y-1 text-sm">
                {[
                  ["Already paid", scenario.recalculate.already_paid],
                  ["Already reserved", scenario.recalculate.already_reserved],
                  ["Outstanding reserve", scenario.recalculate.outstanding_reserve],
                  ["Remaining revenue", scenario.recalculate.remaining_revenue],
                ].map(([label, val]) => val != null && (
                  <div key={label} className="flex justify-between border-b border-white/5 py-1">
                    <span className="text-[#B9C8D5]">{label}</span>
                    <span className="font-mono">{money(val)}</span>
                  </div>
                ))}
                {scenario.recalculate.new_installment_2 && (
                  <div className="mt-2 pt-2 border-t border-[#FFD166]/15">
                    <div className="text-xs text-[#FFD166] mb-1">New Installment 2</div>
                    <PayoutRow entry={{ ...scenario.recalculate.new_installment_2, net_to_bank: scenario.recalculate.new_installment_2.net_to_bank }} idx={0} />
                  </div>
                )}
                {scenario.recalculate.key_rule && (
                  <div className="text-xs text-emerald-300/80 italic pt-1">{scenario.recalculate.key_rule}</div>
                )}
                {scenario.recalculate.note && (
                  <div className="text-xs text-[#B9C8D5]/70 italic pt-1">{scenario.recalculate.note}</div>
                )}
              </div>
            </div>
          )}

          {/* Before/After payment (S6) */}
          {scenario.before_payment && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="text-xs text-red-400 font-semibold uppercase tracking-wider mb-2">Before Affiliate Payment</div>
                <ReconciliationBox recon={scenario.before_payment.reconciliation} />
              </div>
              <div>
                <div className="text-xs text-emerald-400 font-semibold uppercase tracking-wider mb-2">After Affiliate Payment</div>
                {scenario.after_payment?.note && (
                  <div className="p-2 bg-emerald-950/20 rounded border border-emerald-700/20 text-xs text-emerald-300/80 mb-2">
                    {scenario.after_payment.note}
                  </div>
                )}
                <ReconciliationBox recon={scenario.after_payment?.reconciliation} />
              </div>
            </div>
          )}

          {/* Reconciliation (for scenarios without before/after) */}
          {scenario.reconciliation && !scenario.before_payment && (
            <ReconciliationBox recon={scenario.reconciliation} />
          )}

          {/* Stripe verification */}
          <StripeVerification info={scenario.stripe_verification} />
        </div>
      )}
    </div>
  );
}

export function PayoutScenarios() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/admin/payout-scenarios`);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Failed to load scenarios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="payout-scenarios-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[#FFD166]">Payout Scenarios</h1>
          <p className="text-[#B9C8D5] mt-1 text-sm">
            Six demonstration scenarios using live production business logic.
            All calculations are database-only — no Stripe API calls are made.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#FFD166]/25 text-[#FFD166] text-sm hover:bg-[#FFD166]/10 transition"
          data-testid="refresh-scenarios-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {/* Master verification strip */}
      {data && (
        <div className="p-4 rounded-xl border border-amber-600/30 bg-amber-950/15 space-y-2">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            <span className="text-sm font-medium text-amber-300">Live Activation Gate</span>
            <span className="ml-2 px-2 py-0.5 rounded text-xs font-mono bg-red-900/50 text-red-300">
              payout_sweep_enabled = false
            </span>
            <span className="text-xs text-[#B9C8D5]">Generated: {new Date(data.generated_at).toLocaleString()}</span>
          </div>
          <div className="grid grid-cols-3 gap-3 mt-2">
            {Object.entries(data.verification_summary || {}).filter(([k]) => k !== "required_before_live").map(([k, v]) => (
              <div key={k} className="text-xs">
                <span className="text-[#B9C8D5]/60 uppercase tracking-wider text-[10px] block mb-0.5">{k.replace(/_/g, " ")}</span>
                <VerificationBadge label={v} />
              </div>
            ))}
          </div>
          {data.verification_summary?.required_before_live?.length > 0 && (
            <div className="mt-2">
              <div className="text-xs text-amber-300/80 font-medium mb-1">Required before live activation:</div>
              <ul className="list-disc list-inside space-y-0.5">
                {data.verification_summary.required_before_live.map((item, i) => (
                  <li key={i} className="text-xs text-[#B9C8D5]/70">{item}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center gap-3 p-8 justify-center text-[#B9C8D5]">
          <RefreshCw className="w-5 h-5 animate-spin" />
          Computing scenarios…
        </div>
      )}

      {error && (
        <div className="p-4 rounded-xl border border-red-600/30 bg-red-950/15 text-red-300 text-sm">
          <XCircle className="w-4 h-4 inline mr-2" />{error}
        </div>
      )}

      {data && !loading && (
        <div className="space-y-3">
          {data.scenarios.map((scenario) => (
            <ScenarioCard key={scenario.scenario_id} scenario={scenario} />
          ))}
        </div>
      )}

      {/* Footer note */}
      <div className="p-3 rounded-lg border border-[#FFD166]/10 bg-black/20 text-xs text-[#B9C8D5]/60">
        <Info className="w-3 h-3 inline mr-1" />
        All payout amounts, federal reserve calculations, and reconciliation figures above use the exact same
        business-logic functions as the live production system. Numbers will differ from real orders based on
        actual rental amounts, tax rates, and booking dates. No real Stripe, MongoDB, or bank transactions
        were created or modified to generate these scenarios. Federal reserve rate: {data?.federal_reserve_rate || "25%"}.
        Demo delivery date used: {data?.demo_delivery_date || "2026-05-15"}.
      </div>
    </div>
  );
}
